/**
 * Strict US-style date parsing for SmartDateInput (MM/DD/YYYY and 8-digit MMDDYYYY).
 *
 * Mantine's default DateInput parser uses dayjs without strict mode and falls back to
 * `new Date(string)`, which rolls over impossible dates and mis-parses partial years
 * (work items #1385, #1392, #1393).
 */

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { DATE_INPUT_FORMAT, utcTodayDateString } from './dateCalculations';

dayjs.extend(customParseFormat);

/** Earliest calendar year accepted for full-year birthdays (defense in depth; mirrors import rules). */
export const BIRTHDAY_MIN_YEAR = 1900;

/** Shown when the typed text is not a complete, valid calendar date. */
export const US_DATE_INPUT_ERROR_INVALID = 'Enter a valid date (MM/DD/YYYY)';

/** Shown when the date is after maxDate (e.g. birthday in the future). */
export const US_DATE_INPUT_ERROR_AFTER_MAX = 'Date cannot be in the future';

/** Shown when the date is before minDate (birthday / generic bounds). */
export const US_DATE_INPUT_ERROR_BEFORE_MIN = 'Date is too far in the past';

/** Scheduling surfaces (#1413): typed date is before today. */
export const US_DATE_INPUT_ERROR_SCHEDULE_PAST = 'This date is in the past';

/**
 * Scheduling surfaces (#1413): valid calendar date but before `minYmd`.
 * Use {@link formatSchedulingEarliestDateMessage} with the bound.
 */
export const US_DATE_INPUT_ERROR_SCHEDULE_TOO_SOON_PREFIX = 'This date is too soon. The earliest available date is ';

/**
 * Scheduling surfaces (#1413): after `maxYmd`.
 * Use {@link formatSchedulingLatestDateMessage} with the bound.
 */
export const US_DATE_INPUT_ERROR_SCHEDULE_TOO_FAR_PREFIX = 'This date is too far ahead. The latest available date is ';

const EIGHT_DIGITS = /^\d{8}$/;
const SLASH_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * Returns true when month/day/year form a real local calendar date (no rollover).
 */
export function isValidCalendarParts(month: number, day: number, year: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }

  const test = new Date(year, month - 1, day);

  return test.getFullYear() === year && test.getMonth() === month - 1 && test.getDate() === day;
}

export interface ParseUsDateInputOptions {
  /** Inclusive lower bound as YYYY-MM-DD. */
  minYmd?: string | null;
  /** Inclusive upper bound as YYYY-MM-DD. */
  maxYmd?: string | null;
  /** When set, reject years below this (birthday fields). */
  minYear?: number;
  /**
   * When `scheduling`, min/max violations use delivery-style copy (#1413).
   * Inferred automatically when `minYear` is unset and a min/max YMD bound exists.
   */
  boundsMessageStyle?: 'birthday' | 'scheduling';
}

/**
 * Returns user-facing copy for a scheduling min-date violation.
 */
export function formatSchedulingEarliestDateMessage(minYmd: string): string {
  return `${US_DATE_INPUT_ERROR_SCHEDULE_TOO_SOON_PREFIX}${formatYmdForUsDateInput(minYmd)}`;
}

/**
 * Returns user-facing copy for a scheduling max-date violation.
 */
export function formatSchedulingLatestDateMessage(maxYmd: string): string {
  return `${US_DATE_INPUT_ERROR_SCHEDULE_TOO_FAR_PREFIX}${formatYmdForUsDateInput(maxYmd)}`;
}

function resolveBoundsMessageStyle(options?: ParseUsDateInputOptions): 'birthday' | 'scheduling' {
  if (options?.boundsMessageStyle) {
    return options.boundsMessageStyle;
  }

  if (options?.minYear != null) {
    return 'birthday';
  }

  // Scheduling copy is driven by earliest-delivery bounds; max-only caps stay birthday-style.
  if (options?.minYmd) {
    return 'scheduling';
  }

  return 'birthday';
}

function minBoundValidationMessage(ymd: string, minYmd: string, options?: ParseUsDateInputOptions): string {
  if (resolveBoundsMessageStyle(options) === 'scheduling') {
    const todayUtc = utcTodayDateString();
    if (ymd < todayUtc) {
      return US_DATE_INPUT_ERROR_SCHEDULE_PAST;
    }

    return formatSchedulingEarliestDateMessage(minYmd);
  }

  return US_DATE_INPUT_ERROR_BEFORE_MIN;
}

function maxBoundValidationMessage(maxYmd: string, options?: ParseUsDateInputOptions): string {
  if (resolveBoundsMessageStyle(options) === 'scheduling') {
    return formatSchedulingLatestDateMessage(maxYmd);
  }

  return US_DATE_INPUT_ERROR_AFTER_MAX;
}

/**
 * Parses user-typed US date text into `YYYY-MM-DD`, or null when incomplete/invalid.
 * Accepts `MM/DD/YYYY` and compact `MMDDYYYY` (eight digits only).
 */
export function parseUsDateInputToYmd(raw: string, options?: ParseUsDateInputOptions): string | null {
  const text = raw.trim();
  if (!text) {
    return null;
  }

  let candidate = text;
  if (EIGHT_DIGITS.test(text)) {
    candidate = `${text.slice(0, 2)}/${text.slice(2, 4)}/${text.slice(4, 8)}`;
  }

  const match = SLASH_DATE.exec(candidate);
  if (!match) {
    return null;
  }

  const month = parseInt(match[1], 10);
  const day = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);

  if (options?.minYear != null && year < options.minYear) {
    return null;
  }

  if (!isValidCalendarParts(month, day, year)) {
    return null;
  }

  const ymd = `${year}-${pad2(month)}-${pad2(day)}`;

  if (options?.minYmd && ymd < options.minYmd) {
    return null;
  }

  if (options?.maxYmd && ymd > options.maxYmd) {
    return null;
  }

  return ymd;
}

/**
 * Human-readable validation message for a typed US date string and optional bounds.
 */
function parseUsDatePartsFromRaw(raw: string): { month: number; day: number; year: number } | null {
  const text = raw.trim();
  if (!text) {
    return null;
  }

  let candidate = text;
  if (EIGHT_DIGITS.test(text)) {
    candidate = `${text.slice(0, 2)}/${text.slice(2, 4)}/${text.slice(4, 8)}`;
  }

  const match = SLASH_DATE.exec(candidate);
  if (!match) {
    return null;
  }

  return {
    month: parseInt(match[1], 10),
    day: parseInt(match[2], 10),
    year: parseInt(match[3], 10),
  };
}

/**
 * Validates a stored birthday `YYYY-MM-DD` (full year known) against min year and max date.
 */
/**
 * Validates a known-year birthday from typed display text and/or stored `YYYY-MM-DD`.
 * Empty display and empty stored value are allowed (optional birthday).
 */
export function validateKnownYearBirthdayField(params: {
  displayText: string;
  storedYmd: string | null;
  maxYmd: string;
  minYear?: number;
}): string | null {
  const display = params.displayText.trim();
  const ymd = params.storedYmd?.trim() ?? '';
  const minYear = params.minYear ?? BIRTHDAY_MIN_YEAR;
  const parseOpts: ParseUsDateInputOptions = { maxYmd: params.maxYmd, minYear };

  if (display) {
    const displayError = getUsDateInputValidationError(display, parseOpts);
    if (displayError) {
      return displayError;
    }
  }

  if (ymd) {
    return validateFullBirthdayYmd(ymd, { maxYmd: params.maxYmd, minYear });
  }

  if (display) {
    const parsed = parseUsDateInputToYmd(display, parseOpts);
    if (!parsed) {
      return US_DATE_INPUT_ERROR_INVALID;
    }
    return validateFullBirthdayYmd(parsed, { maxYmd: params.maxYmd, minYear });
  }

  return null;
}

export function validateFullBirthdayYmd(
  ymd: string,
  options?: { maxYmd?: string; minYear?: number },
): string | null {
  if (!YMD_PATTERN.test(ymd)) {
    return US_DATE_INPUT_ERROR_INVALID;
  }

  const year = parseInt(ymd.slice(0, 4), 10);
  const month = parseInt(ymd.slice(5, 7), 10);
  const day = parseInt(ymd.slice(8, 10), 10);

  if (!isValidCalendarParts(month, day, year)) {
    return US_DATE_INPUT_ERROR_INVALID;
  }

  const minYear = options?.minYear ?? BIRTHDAY_MIN_YEAR;
  if (year < minYear) {
    return US_DATE_INPUT_ERROR_BEFORE_MIN;
  }

  if (options?.maxYmd && ymd > options.maxYmd) {
    return US_DATE_INPUT_ERROR_AFTER_MAX;
  }

  return null;
}

const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Shown when month/day do not form a real calendar date (year-optional surfaces). */
export const MONTH_DAY_ERROR_INVALID = 'Enter a valid day for the selected month';

/**
 * Validates month/day for yearly occasions (landing reminders, fixed holidays).
 * February 29 is allowed without requiring a leap year.
 */
export function isValidOccasionMonthDay(month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }

  if (month === 2 && day === 29) {
    return true;
  }

  return isValidCalendarParts(month, day, 2001);
}

export function getUsDateInputValidationError(
  raw: string,
  options?: ParseUsDateInputOptions,
): string | null {
  const text = raw.trim();
  if (!text) {
    return null;
  }

  const parts = parseUsDatePartsFromRaw(text);
  if (!parts) {
    return US_DATE_INPUT_ERROR_INVALID;
  }

  if (!isValidCalendarParts(parts.month, parts.day, parts.year)) {
    return US_DATE_INPUT_ERROR_INVALID;
  }

  if (options?.minYear != null && parts.year < options.minYear) {
    return US_DATE_INPUT_ERROR_BEFORE_MIN;
  }

  const ymd = `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;

  if (options?.minYmd && ymd < options.minYmd) {
    return minBoundValidationMessage(ymd, options.minYmd, options);
  }

  if (options?.maxYmd && ymd > options.maxYmd) {
    return maxBoundValidationMessage(options.maxYmd, options);
  }

  return null;
}

/**
 * Formats a `YYYY-MM-DD` value for display using the app date input format.
 */
export function formatYmdForUsDateInput(ymd: string): string {
  return dayjs(ymd, 'YYYY-MM-DD', true).format(DATE_INPUT_FORMAT);
}

/**
 * Builds a Mantine DateInput `dateParser` that never uses JavaScript Date lenient parsing.
 */
export function createStrictUsDateParser(options?: ParseUsDateInputOptions) {
  return (value: string): string | null => parseUsDateInputToYmd(value, options);
}
